import React, { useContext, useMemo, useRef, useState, useEffect, useCallback } from 'react';
import ReactDOMServer from 'react-dom/server';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import { DateTime } from 'luxon';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { PrintIcon, RefreshIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, EditRowIcon } from "../svg/svgIcons";
import AuthContext from '../../components/AuthContext';
import UtilContext from '../../components/UtilContext';
import { MaintenanceMaskDialogCheckbox } from '../../components/maintenance/MaintenanceMaskDialogCheckbox';
import { OverflowMenuProvider } from '../../components/toolbar/OverflowMenu';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import { PanelMenu } from 'primereact/panelmenu';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { MachineStateDot } from '../../components/StatusIndicator';
import { createArrayNavigateHandlers } from '../../utils/arrayNavigate';
import { tabulatorGet } from '../../api/ApiController';
import { getEffectiveMask } from '../../utils/effectiveMask';
import {
  buildMaintenanceMaskAkcieSubItems,
  createConfirmSaveStandardMask,
  createConfirmApplyStandardLayoutToUserMask,
  createOpenMaskDialog,
  createMaskSetFieldHandler,
  createMaskSelectAllHandler,
  createMaskDeselectAllHandler,
  hasApplicableStandardMaskRow,
  clearHeaderFilterForField,
  normalizeColumnVisibilityForSave,
  MAINTENANCE_MASK_DIALOG_SKIP_FIELDS,
} from '../../utils/maintenanceTabulatorMaskActions';
import {
  cleanSorters,
  extractSafePaginationParams,
  createDedupedAjaxRequest,
  refreshProgressiveFromStart,
  safeParse,
  applyCompanyListColumnLayout,
  restoreCompanyListUserMaskToTable,
  mergePagedStandardListFiltersAndSorters,
  appendMaintenanceMachineOutageIntervalsPagedFilter,
} from '../../utils/tabulatorProgressiveLoading';
import {
  buildAuditTabulatorColumns,
  mergeColumnVisibilityWithAuditDefaults,
  TABULATOR_AUDIT_PROFILES,
} from '../../utils/tabulatorAuditColumns';
import { getTabulatorPersistLayoutEvents } from '../../utils/tabulatorPersistLayout';
import { getPusherChannel } from '../../utils/pusherChannel';
import { inputOnEnterHeaderFilter } from '../../utils/inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, TABULATOR_LIST_ID_MIN_WIDTH } from '../../utils/tabulatorColumnMinWidth';
import { primeMultiSelectHeaderFilter } from '../../utils/primeMultiSelectHeaderFilter';
import { defaultProductLogoSrc } from '../../constants/brandingDisplay';
import {
  buildCanonicalPrintFooterHtml,
  buildCanonicalPrintHeaderHtml,
} from '../../utils/printChrome';
import { LocaleContext } from '../../components/LocaleContext';
import { uiT } from '../../utils/uiTranslate';

const MASK_PAGE = 'MaintenanceMachineStateDetail';
const MACHINE_STATE_LABELS = { in_fault: 'V poruche', working_limited: uiT('Common.WorkingLimited'), functional: uiT('Common.Functional') };

const DEFAULT_MAINTENANCE_MACHINE_STATE_DETAIL_COLUMN_VISIBILITY = Object.freeze({
  id: true,
  theme: true,
  since: true,
  until: true,
  state: true,
  created_at: true,
  updated_at: true,
});

/** Poradie dátových stĺpcov (po technických + ID) pre mask layout. */
const MAINTENANCE_MACHINE_STATE_DETAIL_DATA_FIELD_ORDER = Object.freeze([
  'id',
  'theme',
  'since',
  'until',
  'state',
  'created_at',
  'updated_at',
]);

function mergeMaintenanceMachineStateDetailColumnVisibilityFromSaved(saved) {
  return mergeColumnVisibilityWithAuditDefaults(DEFAULT_MAINTENANCE_MACHINE_STATE_DETAIL_COLUMN_VISIBILITY, saved);
}

function applyMaintenanceMachineStateDetailDataColumnVisibility(table, visibility) {
  if (!table) return;
  for (const field of Object.keys(DEFAULT_MAINTENANCE_MACHINE_STATE_DETAIL_COLUMN_VISIBILITY)) {
    if (visibility[field] === false) {
      clearHeaderFilterForField(table, field);
      table.hideColumn?.(field);
    } else {
      table.showColumn?.(field);
    }
  }
  setTimeout(() => table.redraw?.(true), 0);
}

function normalizeMachineStateDetailText(value) {
  return (value ?? '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function machineStateDetailTextHeaderFilter(headerValue, rowValue) {
  if (!headerValue) return true;
  return normalizeMachineStateDetailText(rowValue).includes(normalizeMachineStateDetailText(headerValue));
}

export default function MaintenanceMachineStateDetail({ machine }) {
  const [state,dispatch] = useContext(DatabaseContext);
    const { t } = useContext(LocaleContext);
  const { user } = useContext(AuthContext);
  const {
    openFavoriteDialog,
    setFastView,
    setUserMaskFilter,
    setStandardMaskFilter,
    openChosenNotificationPreferences,
    toast,
    getDateFormat,
    getUserColor,
    getUserInitials,
  } = useContext(UtilContext);

  const [formMachine, setFormMachine] = useState(machine);
  const [columnsVisible, setColumnsVisible] = useState(() => ({ ...DEFAULT_MAINTENANCE_MACHINE_STATE_DETAIL_COLUMN_VISIBILITY }));
  const [maskVisible, setMaskVisible] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState({});

  const ref = useRef(null);
  const tabulatorInstanceRef = useRef(null);
  const overlayRef = useRef(null);
  const inFlightRef = useRef({ sig: null, promise: null });
  const completedCacheRef = useRef(new Map());
  const tableBuiltRef = useRef(false);
  const isRestoringRef = useRef(false);
  const savedHScrollRef = useRef({ left: 0, pending: false });

  const pageSizePc = useMemo(() => {
    const v = (state.module_settings || []).find((s) => s.module === 'global' && s.key === 'page_size_pc')?.value;
    const n = parseInt(v, 10);
    return (Number.isFinite(n) && n > 0) ? n : 50;
  }, [state.module_settings]);

  const dateFormatGlob = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFormatGlob = `${dateFormatGlob} HH:mm`;
  const canSetStandardMask = user?.permissions?.includes('set standard mask');

  const getTable = useCallback(
    () => tabulatorInstanceRef.current ?? ref.current?.table ?? ref.current,
    []
  );
  const refreshFromStart = useCallback(() => refreshProgressiveFromStart(getTable, completedCacheRef, t('Common.LoadingEllipsis')), [getTable]);

  useEffect(() => {
    if (machine) setFormMachine(machine);
  }, [machine]);

  useEffect(() => {
    tableBuiltRef.current = false;
  }, [formMachine?.id]);

  const captureHScroll = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    if (!holder) return;
    savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
  }, [getTable]);

  const restoreHScroll = useCallback(() => {
    if (!savedHScrollRef.current.pending) return;
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    const header = table.columnManager?.contentsElement;
    if (!holder) return;
    const left = savedHScrollRef.current.left;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        holder.scrollLeft = left;
        if (header) header.scrollLeft = left;
        savedHScrollRef.current.pending = false;
      });
    });
  }, [getTable]);

  const currentMachineId = formMachine?.id ?? null;

  useEffect(() => {
    const channel = getPusherChannel();
    if (!channel) return;
    const onOutageIntervalsChanged = (payload) => {
      if (payload?.machine_id != null && payload.machine_id === currentMachineId) {
        captureHScroll();
        refreshFromStart();
      }
    };
    channel.bind('machine_outage_intervals.changed', onOutageIntervalsChanged);
    return () => channel.unbind('machine_outage_intervals.changed', onOutageIntervalsChanged);
  }, [captureHScroll, refreshFromStart, currentMachineId]);

  const buildRequestParams = useCallback((paramsFromTabulator = {}) => {
    const extra = { ...extractSafePaginationParams(paramsFromTabulator) };
    if (extra.size == null) extra.size = pageSizePc;
    mergePagedStandardListFiltersAndSorters(extra, {
      getTable,
      tableBuiltForParams: tableBuiltRef.current,
      userMaskFilters: state.user_mask_filters,
      maskPage: MASK_PAGE,
      appendFilter: appendMaintenanceMachineOutageIntervalsPagedFilter,
    });
    return extra;
  }, [pageSizePc, getTable, state.user_mask_filters]);

  function onEditRow(rowData) {
    const task = rowData?.task;
    if (!task) return;
    dispatch({
      type: 'create_new_tab',
      name: t('Util.Tab.ServiceTaskDetail'),
      translationKey: 'Util.Tab.ServiceTaskDetail',
      legacyLabel: t('Util.Tab.ServiceTaskDetail'),
      page: 'ServiceTaskDetail',
      props: { task },
      mask: 'intr004',
    });
  }

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => onEditRow(rowData)}>
          <EditRowIcon />
        </button>
      </div>
    );
  }

  function MachineStateFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const machineState = rowData.state;
    const label = MACHINE_STATE_LABELS[machineState] || '—';
    return (
      <div className="flex items-center gap-2 min-w-0 overflow-hidden">
        <MachineStateDot task={{ type: 'Error', machine_state: machineState }} />
        <span className="truncate" title={label}>{label}</span>
      </div>
    );
  }

  function formatIsoDateTime(cell) {
    const v = cell.getValue();
    if (!v) return '—';
    const parsed = DateTime.fromISO(String(v));
    return parsed.isValid ? parsed.toFormat(dateTimeFormatGlob) : '—';
  }

  const auditTailColumns = useMemo(
    () =>
      buildAuditTabulatorColumns({
        profile: TABULATOR_AUDIT_PROFILES.createdByUpdatedBy,
        includeId: false,
        dateFormat: dateFormatGlob,
        dateTimeFormat: dateTimeFormatGlob,
        getUserColor,
        getUserInitials,
        remotePassthrough: true,
      }),
    [dateFormatGlob, dateTimeFormatGlob, getUserColor, getUserInitials]
  );

  const columns = useMemo(
    () => [
      {
        title: '',
        field: 'editor',
        hozAlign: 'center',
        width: 44,
        minWidth: 44,
        headerSort: false,
        formatter: reactFormatter(<EditRowFormatter />),
        resizable: false,
        frozen: true,
        print: false,
        download: false,
      },
      {
        title: 'ID',
        field: 'id',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: machineStateDetailTextHeaderFilter,
        minWidth: TABULATOR_LIST_ID_MIN_WIDTH,
        widthGrow: 1,
        sorter: 'number',
        hozAlign: 'left',
      },
      {
        title: t('SupportTicketAdd.Theme'),
        field: 'theme',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: () => true,
        minWidth: tabulatorColumnMinWidthFromTitle(t('SupportTicketAdd.Theme')),
        widthGrow: 1,
      },
      {
        title: 'Od kedy',
        field: 'since',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: () => true,
        formatter: formatIsoDateTime,
        minWidth: tabulatorColumnMinWidthFromTitle('Od kedy'),
        widthGrow: 1,
      },
      {
        title: 'Do kedy',
        field: 'until',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: () => true,
        formatter: formatIsoDateTime,
        minWidth: tabulatorColumnMinWidthFromTitle('Do kedy'),
        widthGrow: 1,
      },
      {
        title: t('Common.MachineStatus'),
        field: 'state',
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.MachineStatus'), 'multiselect'),
        widthGrow: 1,
        formatter: reactFormatter(<MachineStateFormatter />),
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: {
          placeholder: t('Common.MachineStatus'),
          valueType: 'string',
          options: [
            { label: 'V poruche', value: 'in_fault' },
            { label: t('Common.WorkingLimited'), value: 'working_limited' },
            { label: t('Common.Functional'), value: 'functional' },
          ],
        },
        headerFilterFunc: () => true,
      },
      ...auditTailColumns,
    ],
    [dateTimeFormatGlob, auditTailColumns]
  );

  const filteredMachines = useMemo(() => state.machines || [], [state.machines]);
  const { scrollToFirst, scrollUp, scrollDown, scrollToLast } = useMemo(
    () => createArrayNavigateHandlers(filteredMachines, formMachine?.id, setFormMachine),
    [filteredMachines, formMachine?.id]
  );
  const canScroll = filteredMachines.length > 1;

  const closeMenu = useCallback(() => {
    setTimeout(() => {
      OverflowMenuProvider.setVisible?.(null);
      setExpandedKeys({});
    }, 0);
  }, []);

  const hasApplicableStandardMask = useMemo(
    () => hasApplicableStandardMaskRow(state.standard_mask_filters, MASK_PAGE, mergeMaintenanceMachineStateDetailColumnVisibilityFromSaved),
    [state.standard_mask_filters]
  );

  const openMaskDialog = useCallback(
    createOpenMaskDialog({
      columnsVisible,
      setColumnsVisible,
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_STATE_DETAIL_COLUMN_VISIBILITY,
      mergeVisibilityFromSaved: mergeMaintenanceMachineStateDetailColumnVisibilityFromSaved,
      setMaskVisible,
      closeMenu,
    }),
    [columnsVisible, closeMenu]
  );

  const saveUserMaskColumns = useCallback(() => {
    const normalized = normalizeColumnVisibilityForSave(
      DEFAULT_MAINTENANCE_MACHINE_STATE_DETAIL_COLUMN_VISIBILITY,
      columnsVisible
    );
    if (!Object.values(normalized).some(Boolean)) {
      toast?.current?.show({
        severity: 'warn',
        summary: 'Maska',
        detail: t('Common.SelectAtLeastOneColumn'),
        life: 4000,
      });
      return;
    }
    const table = getTable();
    applyMaintenanceMachineStateDetailDataColumnVisibility(table, normalized);
    const filtersSnapshot = table?.getHeaderFilters?.() ?? [];
    setUserMaskFilter(MASK_PAGE, 'all', normalized, filtersSnapshot, null, null);
    setColumnsVisible(normalized);
    setMaskVisible(false);
  }, [columnsVisible, getTable, setUserMaskFilter, toast]);

  const confirmSaveStandardMask = useCallback(
    createConfirmSaveStandardMask({
      maskPage: MASK_PAGE,
      getTable,
      setStandardMaskFilter,
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_STATE_DETAIL_COLUMN_VISIBILITY,
      toast,
    }),
    [getTable, setStandardMaskFilter, toast]
  );

  const confirmApplyStandardLayoutToUserMask = useCallback(
    createConfirmApplyStandardLayoutToUserMask({
      maskPage: MASK_PAGE,
      state,
      setUserMaskFilter,
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_STATE_DETAIL_COLUMN_VISIBILITY,
      mergeVisibilityFromSaved: mergeMaintenanceMachineStateDetailColumnVisibilityFromSaved,
      toast,
      closeMenu,
      onAppliedToTable: ({ colsForSave, attributes }) => {
        const table = getTable();
        if (!table) return;
        isRestoringRef.current = true;
        applyCompanyListColumnLayout(table, attributes, MAINTENANCE_MACHINE_STATE_DETAIL_DATA_FIELD_ORDER);
        setColumnsVisible(colsForSave);
        applyMaintenanceMachineStateDetailDataColumnVisibility(table, colsForSave);
        setTimeout(() => {
          isRestoringRef.current = false;
          table.clearAlert?.();
          captureHScroll();
          refreshFromStart();
        }, 0);
      },
    }),
    [state, closeMenu, getTable, setUserMaskFilter, toast, captureHScroll, refreshFromStart]
  );

  const setMask = useCallback(
    createMaskSetFieldHandler({
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_STATE_DETAIL_COLUMN_VISIBILITY,
      columnsVisible,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applyMaintenanceMachineStateDetailDataColumnVisibility,
      toast,
    }),
    [columnsVisible, getTable, toast]
  );

  const maskSelectAll = useCallback(
    createMaskSelectAllHandler({
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_STATE_DETAIL_COLUMN_VISIBILITY,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applyMaintenanceMachineStateDetailDataColumnVisibility,
    }),
    [getTable]
  );

  const maskDeselectAll = useCallback(
    createMaskDeselectAllHandler({
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_STATE_DETAIL_COLUMN_VISIBILITY,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applyMaintenanceMachineStateDetailDataColumnVisibility,
    }),
    [getTable]
  );

  const itemRenderer = (item, options) => (
    <a className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
      {item.shortcut && <span className="ml-auto border-1 surface-border border-round surface-100 text-xs p-1">{item.shortcut}</span>}
    </a>
  );

  const handleClickOutside = (event) => {
    if (event.target.closest('[data-overflow-trigger]')) return;
    if (overlayRef.current && !overlayRef.current.contains(event.target)) {
      setExpandedKeys({});
      OverflowMenuProvider.setVisible?.(null);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const items = [
    {
      key: '0',
      label: t('Common.Actions'),
      icon: 'pi pi-sliders-v',
      template: itemRenderer,
      items: buildMaintenanceMaskAkcieSubItems({
        maskPage: MASK_PAGE,
        getTable,
        setUserMaskFilter,
        cleanSorters,
        captureHScroll,
        refreshFromStart,
        closeMenu,
        openMaskDialog,
        openChosenNotificationPreferences,
        hasApplicableStandardMask,
        confirmApplyStandardLayoutToUserMask,
        itemRenderer,
      }),
    },
  ];

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="print">
            <button type="button" onClick={() => getTable()?.print?.(false, true)}>
              <PrintIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh">
            <button type="button" onClick={() => { captureHScroll(); refreshFromStart(); }} title={t('Common.Refresh')}>
              <RefreshIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="first">
            <button type="button" className="disabled:opacity-25" disabled={!canScroll} onClick={scrollToFirst}><BackToFirstIcon /></button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="back">
            <button type="button" className="disabled:opacity-25" disabled={!canScroll} onClick={scrollUp}><BackIcon /></button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="forward">
            <button type="button" className="disabled:opacity-25" disabled={!canScroll} onClick={scrollDown}><ForwardIcon /></button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="last">
            <button type="button" className="disabled:opacity-25" disabled={!canScroll} onClick={scrollToLast}><ForwardToLastIcon /></button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite">
            <button type="button" onClick={() => openFavoriteDialog(t('MaintenanceMachineStateDetail.Text.VYPADKY_STROJA'), MASK_PAGE)}>
              <FavoriteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="price">
            <button type="button" onClick={() => setFastView(MASK_PAGE, columnsVisible, columns)}>
              <PriceIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          {canSetStandardMask ? (
            <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
              <button
                type="button"
                title={t('Common.SaveStandardMask')}
                aria-label={t('Common.SaveStandardMask')}
                onClick={confirmSaveStandardMask}
                className="flex items-center justify-center"
              >
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>
      <Splitter className="h-[calc(100vh_-_177px)]" layout="vertical">
        <SplitterPanel className="flex align-items-center justify-content-center overflow-y-auto" minSize={45} size={65}>
          <div className="w-full relative flex flex-col overflow-x p-6 m-4 bg-gray-100 rounded-md shadow-md">
            <h1 className="text-3xl font-semibold text-left text-zinc-900 mb-4">{t('MaintenanceMachineStateDetail.Text.VYPADKY_STROJA')}</h1>
            <label className="block text-md font-semibold text-gray-800">Por.č.: {formMachine?.id}</label>
            <div className="mt-6 md:grid grid-cols-2 gap-2">
              <div>
                <label className="block text-md font-semibold text-gray-800">{t('Common.MachineName')}</label>
                <input name="name" type="text" readOnly value={formMachine?.name ?? ''} className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md" />
              </div>
              <div>
                <label className="block text-md font-semibold text-gray-800">{t('MachineList.Column.MachineTypeName')}</label>
                <input name="machine_type" type="text" readOnly value={formMachine?.machine_type?.name ?? formMachine?.machine_type_name ?? ''} className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md" />
              </div>
              <div>
                <label className="block text-md font-semibold text-gray-800">Typ stroja</label>
                <input name="type" type="text" readOnly value={formMachine?.type ?? ''} className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md" />
              </div>
              <div>
                <label className="block text-md font-semibold text-gray-800">{t('Common.SerialNumber')}</label>
                <input name="serial_number" type="text" readOnly value={formMachine?.serial_number ?? ''} className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md" />
              </div>
            </div>
          </div>
        </SplitterPanel>
        <SplitterPanel className="flex flex-col align-items-center justify-content-center w-full overflow-y-auto" size={75} style={{ minHeight: '110px' }}>
          <div className="px-1 bg-white rounded-md shadow-md w-full h-full">
            <ReactTabulator
              key={formMachine?.id || 'no-machine'}
              data={undefined}
              onRef={(r) => {
                ref.current = r.current;
                tabulatorInstanceRef.current = r.current?.table ?? r.current;
              }}
              className="h-[calc(100%-60px)] w-full"
              columns={columns}
              events={{
                tableBuilt: () => {
                  const table = getTable();
                  if (!table) return;
                  const effective = getEffectiveMask(MASK_PAGE, state);
                  if (effective?.type === 'user' && effective.userMask) {
                    const m = effective.userMask;
                    isRestoringRef.current = true;
                    restoreCompanyListUserMaskToTable(table, m, {
                      canonicalFieldOrder: MAINTENANCE_MACHINE_STATE_DETAIL_DATA_FIELD_ORDER,
                      setColumnsVisible,
                      clearHeaderFiltersWhenEmpty: true,
                      applyColumnVisibilityFn: (tbl, jc, setVis) => {
                        const merged = mergeMaintenanceMachineStateDetailColumnVisibilityFromSaved(jc);
                        setVis(merged);
                        applyMaintenanceMachineStateDetailDataColumnVisibility(tbl, merged);
                      },
                    });
                    const merged = mergeMaintenanceMachineStateDetailColumnVisibilityFromSaved(safeParse(m.columns, {}));
                    for (const field of Object.keys(DEFAULT_MAINTENANCE_MACHINE_STATE_DETAIL_COLUMN_VISIBILITY)) {
                      if (merged[field] === false) clearHeaderFilterForField(table, field);
                    }
                    setTimeout(() => { isRestoringRef.current = false; }, 0);
                  } else if (effective?.type === 'standard' && effective.standardMask) {
                    const m = effective.standardMask;
                    isRestoringRef.current = true;
                    const attributes = safeParse(m.attributes, []);
                    const jsonColumns = safeParse(m.columns, {});
                    applyCompanyListColumnLayout(table, attributes, MAINTENANCE_MACHINE_STATE_DETAIL_DATA_FIELD_ORDER);
                    if (jsonColumns && typeof jsonColumns === 'object') {
                      const merged = mergeMaintenanceMachineStateDetailColumnVisibilityFromSaved(jsonColumns);
                      setColumnsVisible(merged);
                      applyMaintenanceMachineStateDetailDataColumnVisibility(table, merged);
                    }
                    setTimeout(() => { isRestoringRef.current = false; }, 0);
                  }
                  tableBuiltRef.current = true;
                },
                ...getTabulatorPersistLayoutEvents(MASK_PAGE, tabulatorInstanceRef, setUserMaskFilter),
                headerFilterChanged: () => {
                  if (!isRestoringRef.current) captureHScroll();
                },
                sortChanged: () => {
                  if (!isRestoringRef.current) captureHScroll();
                },
                renderComplete: () => restoreHScroll(),
              }}
              options={{
                layout: 'fitColumns',
                renderHorizontal: 'virtual',
                movableColumns: true,
                headerSortTristate: true,
                rowHeight: 42,
                printAsHtml: true,
                printRowRange: 'active',
                printCopyStyle: true,
                ajaxURL: formMachine?.id ? `/api/machines/${formMachine.id}/outage-intervals/paged` : '',
                ajaxParams: () => ({}),
                ajaxRequestFunc: createDedupedAjaxRequest(buildRequestParams, tabulatorGet, inFlightRef, completedCacheRef),
                filterMode: 'remote',
                sortMode: 'remote',
                progressiveLoad: 'scroll',
                progressiveLoadScrollMargin: 80,
                paginationSize: pageSizePc,
                pagination: false,
                dataLoader: true,
                dataLoaderLoading: t('Common.LoadingEllipsis'),
                dataLoaderError: t('Common.LoadFailed'),
                ajaxRequesting: () => {
                  captureHScroll();
                  const table = getTable();
                  if (table?.initialized) table.alert?.(t('Common.LoadingEllipsis'));
                  return true;
                },
                ajaxResponse: (url, params, response) => {
                  getTable()?.clearAlert?.();
                  return response;
                },
                ajaxError: () => {
                  getTable()?.clearAlert?.();
                },
                printHeader: function () {
            const bodyHtml = ReactDOMServer.renderToStaticMarkup(
                    <div className="w-full text-[11px]">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="space-y-2">
                          <div>
                            <label className="block text-[11px] text-left font-semibold text-gray-800">{t('Ui.R67.Por_c')}</label>
                            <input type="text" readOnly value={formMachine?.id ?? ''} className="mt-1 block w-full px-2 text-[11px] bg-white border border-gray-300 rounded-md" />
                          </div>
                          <div>
                            <label className="block text-[11px] text-left font-semibold text-gray-800">{t('Common.MachineName')}</label>
                            <input type="text" readOnly value={formMachine?.name ?? ''} className="mt-1 block w-full px-2 text-[11px] bg-white border border-gray-300 rounded-md" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-[11px] text-left font-semibold text-gray-800">{t('MachineList.Column.MachineTypeName')}</label>
                            <input type="text" readOnly value={formMachine?.machine_type?.name ?? formMachine?.machine_type_name ?? ''} className="mt-1 block w-full px-2 text-[11px] bg-white border border-gray-300 rounded-md" />
                          </div>
                          <div>
                            <label className="block text-[11px] text-left font-semibold text-gray-800">Typ stroja</label>
                            <input type="text" readOnly value={formMachine?.type ?? ''} className="mt-1 block w-full px-2 text-[11px] bg-white border border-gray-300 rounded-md" />
                          </div>
                          <div>
                            <label className="block text-[11px] text-left font-semibold text-gray-800">{t('Common.SerialNumber')}</label>
                            <input type="text" readOnly value={formMachine?.serial_number ?? ''} className="mt-1 block w-full px-2 text-[11px] bg-white border border-gray-300 rounded-md" />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
            return buildCanonicalPrintHeaderHtml({
                branding: state?.branding,
                productFallbackSrc: defaultProductLogoSrc,
                companySettingsPrint: state?.company_settings_print,
                moduleSettings: state?.module_settings,
                title: t('Print.Title.MaintenanceMachineStateDetail'),
                printDateLabel: t('Print.DateLabel'),
                bodyHtml,
            });
        },
                printFooter: function () {
                    return buildCanonicalPrintFooterHtml({ companySettingsPrint: state?.company_settings_print });
                },
              }}
            />
          </div>
        </SplitterPanel>
      </Splitter>
      <Dialog header={t('Common.SetMask')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-2">{t('MaintenanceTemplateList.MaskHelp')}</p>
          <div className="flex flex-wrap gap-2 mb-2">
            <button type="button" onClick={maskSelectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.SelectAll')}</button>
            <button type="button" onClick={maskDeselectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.DeselectAllExceptId')}</button>
          </div>
          {columns.map((item) => {
            if (!item?.field || MAINTENANCE_MASK_DIALOG_SKIP_FIELDS.has(item.field)) return null;
            return (
              <div key={item.field} className="flex flex-row border border-black rounded-md p-2 m-1">
                <MaintenanceMaskDialogCheckbox field={item.field} inputId={`mmsd-mask-${item.field}`} title={item.title || item.field} columnsVisible={columnsVisible} onChange={setMask} />
                <label htmlFor={`mmsd-mask-${item.field}`} className={`ml-2 ${item.field === 'id' ? '' : 'cursor-pointer'}`}>{item.title}</label>
              </div>
            );
          })}
          <button type="button" onClick={saveUserMaskColumns} className="m-1 px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-zinc-700">{t('Common.SaveUpper')}</button>
        </div>
      </Dialog>
    </>
  );
}
